import { useEffect } from 'react';
import { usePreventRemove } from '@react-navigation/native';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { Platform } from 'react-native';

export interface UnsavedLeaveLabels {
  title: string;
  message: string;
  stay: string;
  discard: string;
}

interface DialogButton {
  text: string;
  variant?: string;
  action?: string;
  style?: string;
  onPress?: () => void;
}

interface DialogProps {
  title: string;
  message: string;
  buttons?: DialogButton[];
}

export interface UseUnsavedLeaveGuardOptions {
  isDirty: boolean;
  isAutosaveInFlight?: boolean;
  labels: UnsavedLeaveLabels;
  showDialog: (props: DialogProps) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigation: NavigationProp<ParamListBase> | any;
}

/**
 * Guards against accidental data loss when navigating away from a screen with
 * unsaved changes. Covers two exit paths:
 *  1. In-app navigation (stack pop, back button, drawer change) via `usePreventRemove`.
 *  2. Browser tab close / refresh (web only) via the `beforeunload` event.
 *
 * Labels are injected so the same hook serves student and lecturer contexts.
 */
export default function useUnsavedLeaveGuard({
  isDirty,
  isAutosaveInFlight = false,
  labels,
  showDialog,
  navigation,
}: UseUnsavedLeaveGuardOptions): void {
  usePreventRemove(isDirty && !isAutosaveInFlight, ({ data }) => {
    showDialog({
      title: labels.title,
      message: labels.message,
      buttons: [
        { text: labels.stay, action: 'secondary' },
        {
          text: labels.discard,
          style: 'destructive',
          action: 'negative',
          onPress: () => navigation.dispatch(data.action),
        },
      ],
    });
  });

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return () => {};
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty || isAutosaveInFlight) {
        e.preventDefault();
        // Required for Chrome/Edge; assignment is intentional even though the
        // value is ignored by modern browsers (they show a generic message).
        e.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isAutosaveInFlight]);
}
